import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { ProductBundle } from '@eti/products';
import { TravelerNew } from '@eti/types';
import { css, etiColors, mediaQueries, resetButtonStyle } from '@eti/styles';
import { Icon } from '@eti/components';
import { Dialog } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { AVAILABLE_BUNDLE } from '../constants/resultConstants';
import { RESULT } from '../../../constants/formNamesConstants';
import { pushViewItemToDataLayer } from '../../../common/data-layer/ecommerce/pushViewItemToDataLayer';

const closeButtonWrapperStyles = css`
  align-items: center;
  cursor: pointer;
  display: flex;
  ${resetButtonStyle};
  padding: 10px;
  position: absolute;
  right: 0;
  top: 0;

  [dir='rtl'] & {
    left: 0;
    right: initial;
  }
`;

const bundleDialogStyles = css`
  width: 100%;

  @media ${mediaQueries.medium.up} {
    max-width: 95%;
  }

  @media ${mediaQueries.large.up} {
    max-width: 960px;
  }

  @media ${mediaQueries.xlarge.up} {
    max-width: 1000px;
  }
`;

const AncillaryBundleModal = ({
  addTripForSelectedTrip,
  bundleId,
  discountPercentage,
  form,
  isOpen,
  onClose,
  options,
  selectedExtraProducts,
  selectionKey,
  travelers,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      pushViewItemToDataLayer('ANCILLARY_BUNDLE', bundleId, options);
    }
  }, [isOpen, bundleId, options]);

  const handleOnConfirm = async (value) => {
    const { price, id: optionId } = value || {};
    setLoading(true);
    try {
      await addTripForSelectedTrip({
        variables: {
          selectionKey,
          ...(price && optionId && { bundleProductSelection: [{ bundleId, optionId }] }),
          ...(selectedExtraProducts && {
            extraProductSelection: selectedExtraProducts
              ?.filter(
                (product) =>
                  product.selectedWithTrip ||
                  product.configuration.productPreSelection === 'YES_SELECTED',
              )
              ?.map(({ productId }) => Number(productId)),
          }),
        },
      });
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <Dialog
      className={bundleDialogStyles}
      closeButtonAriaLabel={t('ScreenReader.CloseButtonDialog.AriaLabel.Text')}
      isOpen={isOpen}
    >
      <ProductBundle
        discountPercentage={discountPercentage}
        form={form}
        isLoading={loading}
        onConfirm={handleOnConfirm}
        options={options}
        shouldShowConfirmationButton
        travelers={travelers}
      />
      <button
        className={closeButtonWrapperStyles}
        data-testid="ancillaryBundleModal-close-button"
        onClick={onClose}
        type="button"
      >
        <Icon color={etiColors.grayScale[8]} id="close-menu" size="16px" />
      </button>
    </Dialog>
  );
};

AncillaryBundleModal.propTypes = {
  addTripForSelectedTrip: PropTypes.func,
  bundleId: PropTypes.string,
  discountPercentage: PropTypes.number,
  form: PropTypes.string,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      features: PropTypes.arrayOf(
        PropTypes.shape({
          isIncluded: PropTypes.bool,
          text: PropTypes.string,
        }),
      ),
      id: PropTypes.string,
      priceRaw: PropTypes.number,
      title: PropTypes.string,
    }),
  ),
  selectedExtraProducts: PropTypes.arrayOf(Object),
  selectionKey: PropTypes.string,
  travelers: PropTypes.arrayOf(TravelerNew),
};

const formSelector = formValueSelector(RESULT);

const mapStateToProps = (state) => {
  const { options, id, selectionKey, discountPercentage } =
    formSelector(state, AVAILABLE_BUNDLE) || {};
  return { options, bundleId: id, selectionKey, discountPercentage };
};

export default connect(mapStateToProps)(AncillaryBundleModal);
